import { useEffect, useState } from 'react';
import type { AdminPayment, ApiClient } from '../api';
import { formatDate, formatMoney, paymentTag, promptForReason, renderError } from './shared';

const PAGE = 50;

/**
 * Every payment, across accounts. The "problems" filters are the two things
 * an operator should look at: PayU confirming an amount we did not ask for,
 * and payments still pending after an hour. The only action is asking PayU
 * again — a payment's status is never set by hand.
 */
export function PaymentsView({ client, onOpenUser }: { client: ApiClient; onOpenUser: (id: string) => void }) {
  const [status, setStatus] = useState('');
  const [problem, setProblem] = useState('');
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<{ total: number; payments: AdminPayment[] } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    client
      .listPayments({ status, problem, q: submitted, offset })
      .then((response) => !cancelled && setData(response))
      .catch((err) => !cancelled && setError(err));
    return () => {
      cancelled = true;
    };
  }, [client, status, problem, submitted, offset, tick]);

  async function recheck(payment: AdminPayment) {
    const reason = await promptForReason('re-checking this payment with PayU');
    if (!reason) return;
    setBusy(payment.id);
    setError(null);
    try {
      await client.recheckPayment(payment.id, reason);
      setTick((value) => value + 1);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">Billing</p>
          <h2>Payments</h2>
          <p className="muted">Checkout payments across all accounts. Refunds are made in the PayU panel.</p>
        </div>
      </div>

      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          setOffset(0);
          setSubmitted(query.trim());
        }}
      >
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Payment id, PayU order id, email or account id" />
        <select value={status} onChange={(event) => { setStatus(event.target.value); setOffset(0); }} aria-label="Status">
          <option value="">Any status</option>
          <option value="pending">Pending</option>
          <option value="completed">Paid</option>
          <option value="cancelled">Cancelled</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
        <select value={problem} onChange={(event) => { setProblem(event.target.value); setOffset(0); }} aria-label="Problems">
          <option value="">All payments</option>
          <option value="amount_mismatch">Problem: amount mismatch</option>
          <option value="stale">Problem: pending over 1 hour</option>
        </select>
        <button type="submit">Search</button>
      </form>

      {error !== null && renderError(error)}

      <div className="panel">
        {data === null ? (
          <p className="muted">Loading…</p>
        ) : data.payments.length === 0 ? (
          <p className="muted">No payments match.</p>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Account</th>
                  <th>What</th>
                  <th>Method</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>PayU order</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{formatDate(payment.createdAt)}</td>
                    <td>
                      {payment.user ? (
                        <button className="link" onClick={() => onOpenUser(payment.user!.id)}>
                          {payment.user.email ?? payment.user.displayName ?? payment.user.id.slice(0, 8)}
                        </button>
                      ) : (
                        <span className="muted">deleted account</span>
                      )}
                    </td>
                    <td>
                      {payment.description}
                      <div className="muted small">{payment.priceKey}</div>
                    </td>
                    <td>{payment.method ?? '—'}</td>
                    <td>{formatMoney(payment.amountMinor, payment.currency)}</td>
                    <td title={payment.providerStatus ?? undefined}>{paymentTag(payment.status, payment.providerStatus)}</td>
                    <td>
                      <code>{payment.providerOrderId ?? '—'}</code>
                    </td>
                    <td>
                      {payment.status === 'pending' && (
                        <button disabled={busy !== null} onClick={() => void recheck(payment)}>
                          {busy === payment.id ? 'Asking PayU…' : 'Re-check'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager total={data.total} offset={offset} onChange={setOffset} />
          </>
        )}
      </div>
    </>
  );
}

export function Pager({ total, offset, onChange }: { total: number; offset: number; onChange: (offset: number) => void }) {
  if (total <= PAGE) return <p className="muted small">{total} total</p>;
  return (
    <div className="pager">
      <button disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - PAGE))}>
        ← Newer
      </button>
      <span className="muted">
        {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
      </span>
      <button disabled={offset + PAGE >= total} onClick={() => onChange(offset + PAGE)}>
        Older →
      </button>
    </div>
  );
}
